import React, { createContext, useContext, useCallback, useMemo } from "react";
import { t, createScopedT, getTranslations, type SupportedLanguage } from "./index";

interface I18nContextValue {
  /** Current language */
  language: SupportedLanguage;
  /** Translate a key */
  t: typeof t;
  /** Create a scoped translator */
  createScopedT: typeof createScopedT;
  /** Get all translations for a namespace */
  getTranslations: typeof getTranslations;
}

const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  children: React.ReactNode;
  language?: SupportedLanguage;
}

export function I18nProvider({ children, language = "ar" }: I18nProviderProps) {
  // Wrap t function to always use the provider's language
  const translate = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      return t(key, params, language);
    },
    [language]
  );

  // Wrap createScopedT to use the provider's language
  const scopedT = useCallback(
    (namespace: string) => {
      return (key: string, params?: Record<string, string | number>) => {
        return t(`${namespace}.${key}`, params, language);
      };
    },
    [language]
  );

  // Wrap getTranslations to use the provider's language
  const getTranslationsForLang = useCallback(
    (namespace: string) => {
      return getTranslations(namespace, language);
    },
    [language]
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      t: translate,
      createScopedT: scopedT,
      getTranslations: getTranslationsForLang,
    }),
    [language, translate, scopedT, getTranslationsForLang]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Hook to access i18n functions
 */
export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}

/**
 * Hook to safely access i18n (returns null if not in provider)
 * Falls back to default language functions
 */
export function useI18nSafe(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    // Return default implementation
    return {
      language: "ar",
      t: (key: string, params?: Record<string, string | number>) => t(key, params),
      createScopedT: createScopedT,
      getTranslations: getTranslations,
    };
  }
  return context;
}
